import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { SEGMENT_BUILDER_TOUR } from "./segmentBuilderTour";

import { ROUTES } from "@/constants/routes";
import { segmentsQueryKeys } from "@/features/segments/api/queryKeys";
import { useSegmentPaths } from "@/features/segments/builder/hooks/useSegmentPaths";
import { TOUR_PARAM } from "@/features/tours/constants";
import { instance } from "@/lib/axios/instance";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";

const latestSegmentResponseSchema = z.object({
  data: z.array(z.object({ id: z.string() })),
});

type UseSegmentTourLinkParams = {
  enabled: boolean;
};

export function useSegmentTourLink({ enabled }: UseSegmentTourLinkParams) {
  const { clientId } = useParams();
  const { canEdit, canCreate } = usePermissionMatrix();
  const paths = useSegmentPaths();

  const { data: segmentId, refetch } = useQuery({
    queryKey: segmentsQueryKeys.tourLink(clientId),
    queryFn: async () => {
      const response = await instance.get("/api/v2/Segments", {
        params: { clientId, pageSize: 1, orderBy: "updatedDate", descending: true },
      });
      return latestSegmentResponseSchema.parse(response.data).data[0]?.id ?? null;
    },
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const toLink = (id: string | null | undefined) => {
    if (id === undefined) return null;
    const search = `?${new URLSearchParams({ [TOUR_PARAM]: SEGMENT_BUILDER_TOUR.id })}`;
    if (id) return `${canEdit("segments") ? paths.edit(id) : paths.view(id)}${search}`;
    if (canCreate("segments")) return `${paths.overview}/${ROUTES.new}${search}`;
    return null;
  };

  const getFreshLink = async () => {
    const { data: fresh } = await refetch();
    return toLink(fresh === undefined ? segmentId : fresh);
  };

  return { link: toLink(segmentId), getFreshLink };
}
